import { useCallback, useState } from "react";
import ModusSelectSessionChips from "./ModusSelectSessionChips";
import { useHuiswerkModusContext } from "@/context/HuiswerkModusContext";
import { useRouter } from "next/navigation";
import { useAppContext } from "@/context/AppContext";

export interface ExpandedHuiswerkModusSelectProps {
  setExpanded: (val: boolean) => void;
}

export default function ExpandedHuiswerkModusSelect({
  setExpanded
}: ExpandedHuiswerkModusSelectProps) {
  const { selectModus } = useAppContext();
  const { restoreHuiswerkSession, clearHuiswerkSession } =
    useHuiswerkModusContext();

  const router = useRouter();

  const [showConfirmReplace, setShowConfirmReplace] = useState(false);

  const handleContinueHuiswerk = useCallback(() => {
    restoreHuiswerkSession();
    router.push("/library");
  }, [restoreHuiswerkSession, router]);

  const handleDelete = useCallback(() => {
    clearHuiswerkSession();
    setExpanded(false);
  }, [clearHuiswerkSession, setExpanded]);

  const handleConfirmReplace = useCallback(() => {
    clearHuiswerkSession();
    setShowConfirmReplace(false);
    selectModus("huiswerk", "expanded_new");
    router.push("/library");
  }, [clearHuiswerkSession, selectModus, router]);

  const handleNewHuiswerk = useCallback(() => {
    setShowConfirmReplace(true);
  }, []);

  return (
    <div className="min-h-0 overflow-hidden">
      <div className="flex max-h-full min-h-0 flex-col gap-3 overflow-hidden px-4 pb-5 pt-4">
        {/* New huiswerk button / confirm replace */}
        {showConfirmReplace ? (
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-huiswerk/25 bg-white/80">
            <div className="flex-1">
              <span className="text-sm font-semibold text-gray-800 block">
                Je bestaande set wordt overschreven
              </span>
              <span className="text-xs text-gray-400">
                Wil je een nieuwe huiswerkset maken?
              </span>
              <div className="flex gap-2 mt-2.5 justify-center">
                <button
                  type="button"
                  onClick={handleConfirmReplace}
                  className="px-4 py-2 rounded-lg bg-huiswerk text-white text-sm font-semibold hover:bg-huiswerk/90 transition-colors cursor-pointer"
                >
                  Ja, nieuwe set
                </button>
                <button
                  type="button"
                  onClick={() => setShowConfirmReplace(false)}
                  className="px-4 py-2 rounded-lg bg-white text-gray-600 text-sm font-semibold border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Annuleren
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleNewHuiswerk}
            className="flex items-center gap-3 px-4 py-3 rounded-xl border border-huiswerk/25 bg-white/80 hover:bg-white hover:border-huiswerk/50 hover:shadow-md transition-all cursor-pointer text-left"
          >
            <div className="w-9 h-9 rounded-lg bg-huiswerk/10 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-5 h-5 text-huiswerk"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </div>
            <div>
              <span className="text-sm font-semibold text-gray-800 block">
                Nieuwe huiswerkset
              </span>
              <span className="text-xs text-gray-400">
                Kies zelf welke opgaven je wilt maken
              </span>
            </div>
          </button>
        )}

        {/* Divider */}
        <div className="flex items-center gap-2 px-1">
          <div className="flex-1 border-t border-huiswerk/15" />
          <span className="text-[10px] font-semibold text-huiswerk/50 uppercase tracking-wider">
            of ga verder
          </span>
          <div className="flex-1 border-t border-huiswerk/15" />
        </div>

        {/* Saved session card */}
        <div
          role="button"
          tabIndex={0}
          onClick={handleContinueHuiswerk}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleContinueHuiswerk();
            }
          }}
          className="relative min-h-0 overflow-y-auto rounded-xl border border-huiswerk/20 bg-white/70 transition-all duration-200 hover:border-huiswerk/40 hover:bg-white hover:shadow-md cursor-pointer"
        >
          {/* Delete button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete();
            }}
            className="absolute top-0 right-0 p-2 rounded-bl-lg rounded-tr-xl border-b border-l border-huiswerk/20 text-red-500 hover:text-red-600 hover:bg-red-50 hover:border-red-300 transition-all cursor-pointer"
            aria-label="Verwijder set"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>

          <ModusSelectSessionChips />
        </div>
      </div>
    </div>
  );
}
